// frontend/src/pages/freelancer/FreelancerServiceAdd.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import "./freelancerStyles.css";
import PriceTiersEditor from "../../components/PriceTiersEditor";

// Utility function to convert text to user-friendly Title Case
const toUserFriendly = (text) => {
  if (!text) return "";
  return text
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function FreelancerServiceAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    durationMinutes: "",
    category: "other",
    serviceImage: "",
    applicableTo: [],
    pricingType: "fixed",
    maxPrice: "",
    hourlyRate: "",
    maxHoursPerBooking: 8,
    priceTiers: [],
    requiresConsultation: false,
    platformTemplateId: null, // Track platform template usage
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [staffProfessionalTypes, setStaffProfessionalTypes] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Fetch staff profile to get professional types and service categories
  useEffect(() => {
    const fetchStaffProfileAndCategories = async () => {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      try {
        // Fetch staff profile for professional types
        const profileResponse = await axios.get(
          `${baseURL}/api/freelancer/profile`,
          {
            headers,
          },
        );
        const staff = profileResponse.data.staff;
        const types = staff?.professionalTypes || [];
        setStaffProfessionalTypes(types);

        // Prefer business types returned by /api/freelancer/profile.
        // For R7 this is resolved from booth-rental location on the backend.
        let businessTypes = Array.isArray(staff.businessTypes)
          ? staff.businessTypes
          : [];

        // Backward-compatible fallback for non-R7 profiles that do not include businessTypes.
        if (
          (!businessTypes || businessTypes.length === 0) &&
          staff.businessId
        ) {
          try {
            const businessResponse = await axios.get(
              `${baseURL}/api/business/${staff.businessId}`,
              { headers },
            );
            businessTypes = businessResponse.data?.type || [];
          } catch (bizErr) {
          }
        }

        const fetchByBusinessTypes = async (typeValues) => {
          if (!Array.isArray(typeValues) || typeValues.length === 0) return [];
          const businessCategoriesParam = typeValues.join(",");
          const categoriesResponse = await axios.get(
            `${baseURL}/api/platform-settings/service-categories?businessCategories=${businessCategoriesParam}`,
            { headers },
          );
          return categoriesResponse.data.categories || [];
        };

        try {
          let fetchedCategories = [];
          let resolvedTypes = businessTypes;

          if (businessTypes && businessTypes.length > 0) {
            fetchedCategories = await fetchByBusinessTypes(businessTypes);
          }

          // If direct business type lookup returns nothing, derive business categories from professional types.
          if (fetchedCategories.length === 0 && types.length > 0) {
            const professionalTypesResponse = await axios.get(
              `${baseURL}/api/platform-settings/professional-types/public`,
              { headers },
            );
            const professionalTypesCatalog =
              professionalTypesResponse.data.professionalTypes || [];

            const derivedTypeSet = new Set();
            professionalTypesCatalog.forEach((pt) => {
              if (
                types.includes(pt.value) &&
                Array.isArray(pt.businessCategories)
              ) {
                pt.businessCategories.forEach((bc) => derivedTypeSet.add(bc));
              }
            });

            const derivedTypes = Array.from(derivedTypeSet);
            if (derivedTypes.length > 0) {
              fetchedCategories = await fetchByBusinessTypes(derivedTypes);
              resolvedTypes = derivedTypes;
            }
          }

          // Independent pros (freelancers / booth renters) have no business of
          // their own to filter by - fall back to ALL service categories so
          // they can pick any category.
          if (fetchedCategories.length === 0) {
            try {
              const allResponse = await axios.get(
                `${baseURL}/api/platform-settings/service-categories?businessCategories=all`,
                { headers },
              );
              if (
                Array.isArray(allResponse.data.categories) &&
                allResponse.data.categories.length > 0
              ) {
                fetchedCategories = allResponse.data.categories;
              }
            } catch (allErr) {
            }
          }

          if (fetchedCategories.length > 0) {
            setServiceCategories(fetchedCategories);
            // Keep selected category in sync with server-provided categories
            setFormData((prev) => {
              const hasCurrent = fetchedCategories.some(
                (cat) => cat.value === prev.category,
              );
              return hasCurrent
                ? prev
                : { ...prev, category: fetchedCategories[0].value };
            });
          } else {
            setServiceCategories([]);
            setErrorMsg(
              "No service categories are configured for this account. Please contact support.",
            );
          }
        } catch (catErr) {
          setServiceCategories([]);
          setErrorMsg(
            "Unable to fetch service categories right now. Please refresh and try again.",
          );
        }
      } catch (err) {
        setServiceCategories([]);
        setErrorMsg(
          "Unable to load your service categories. Please refresh and try again.",
        );
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchStaffProfileAndCategories();
  }, [getAuthHeaders, getApiBaseUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages on input change
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleApplicableToToggle = (type) => {
    setFormData((prev) => {
      const isSelected = prev.applicableTo.includes(type);
      const newApplicableTo = isSelected
        ? prev.applicableTo.filter((t) => t !== type)
        : [...prev.applicableTo, type].slice(0, 3);
      return { ...prev, applicableTo: newApplicableTo };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrorMsg("Please select an image file");
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg("Image must be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setErrorMsg("");
    }
  };


  // Image upload function - COMMENTED OUT PER USER REQUEST
  /*
  const uploadImage = async () => {
    if (!imageFile) return null;

    setUploadingImage(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const formData = new FormData();
      formData.append("image", imageFile);

      const res = await axios.post(
        `${baseURL}/api/staff/services/upload-image`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data.imageUrl;
    } catch (err) {
      throw new Error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };
  */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Validate inputs
      if (!formData.name.trim()) {
        setErrorMsg("Service name is required");
        setSaving(false);
        return;
      }

      if (
        formData.pricingType !== "range" &&
        formData.pricingType !== "hourly" &&
        (!formData.price || parseFloat(formData.price) < 0)
      ) {
        setErrorMsg("Valid price is required");
        setSaving(false);
        return;
      }

      if (
        formData.pricingType !== "hourly" &&
        formData.pricingType !== "range" &&
        (!formData.durationMinutes || parseInt(formData.durationMinutes) < 5)
      ) {
        setErrorMsg("Duration must be at least 5 minutes");
        setSaving(false);
        return;
      }

      if (!serviceCategories.length) {
        setErrorMsg(
          "Service categories are not loaded yet. Please refresh and try again.",
        );
        setSaving(false);
        return;
      }

      // Upload image if provided - COMMENTED OUT PER USER REQUEST
      /*
      let uploadedImageUrl = null;
      if (imageFile) {
        try {
          uploadedImageUrl = await uploadImage();
        } catch (err) {
          setErrorMsg("Failed to upload image. Please try again.");
          setSaving(false);
          return;
        }
      }
      */

      // Send create request
      const serviceData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        durationMinutes: parseInt(formData.durationMinutes),
        category: formData.category,
        // serviceImage: uploadedImageUrl, // COMMENTED OUT - no image upload
        pricingType: formData.pricingType,
        platformTemplateId: formData.platformTemplateId || null, // Track template usage
      };

      // Add conditional pricing fields
      // F2: hourly = rate x hours; range = customer picks one of the options
      if (formData.pricingType === "hourly") {
        const rate = parseFloat(formData.hourlyRate);
        if (Number.isFinite(rate)) {
          serviceData.hourlyRate = rate;
          serviceData.price = rate;
        }
        serviceData.maxHoursPerBooking = parseInt(
          formData.maxHoursPerBooking || 8,
          10,
        );
        // Duration field is hidden for hourly - the engine bills 60 min per hour.
        serviceData.durationMinutes = 60;
      }
      if (formData.pricingType === "range") {
        const usableTiers = (formData.priceTiers || [])
          .map((tier) => ({
            label: String(tier.label || "").trim(),
            price: parseFloat(tier.price),
            durationMinutes: parseInt(tier.durationMinutes, 10),
          }))
          .filter((tier) => tier.label && Number.isFinite(tier.price));

        if (usableTiers.length < 2) {
          setErrorMsg(
            "Add at least 2 priced options (for example: Shoulder length $150, Mid-back $220).",
          );
          return;
        }

        const missingMinutes = usableTiers.some(
          (tier) =>
            !Number.isFinite(tier.durationMinutes) || tier.durationMinutes < 5,
        );
        if (missingMinutes) {
          setErrorMsg("Give every option a duration of at least 5 minutes.");
          return;
        }

        const tierPrices = usableTiers.map((t) => t.price);
        serviceData.priceTiers = usableTiers;
        serviceData.price = Math.min(...tierPrices);
        serviceData.maxPrice = Math.max(...tierPrices);
        // Duration field is hidden for range - use the longest option length.
        serviceData.durationMinutes = Math.max(
          ...usableTiers.map((t) => Number(t.durationMinutes) || 0),
        );
      }
      if (formData.pricingType === "variable") {
        serviceData.requiresConsultation = formData.requiresConsultation;
      }
      // F1: per-unit service - base price is the per-unit price; max 10 units
      if (formData.pricingType === "per_unit") {
        serviceData.pricePerUnit = parseFloat(formData.price);
        serviceData.maxUnitsPerBooking = 10;
      }

      await axios.post(`${baseURL}/api/staff/services`, serviceData, {
        headers,
      });

      setSuccessMsg("Service created successfully!");

      // Redirect back to services list after 1.5 seconds
      setTimeout(() => {
        navigate(`${basePath}/services`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Unable to create service. Please try again.";
        setErrorMsg(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`${basePath}/services`);
  };

  return (
    <FreelancerSidebarLayout>
      <div style={{ maxWidth: "700px", margin: "0 auto", padding: "20px" }}>
        {/* Back Button */}
        <button
          onClick={() => navigate(`${basePath}/services`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          <span>←</span> Back to Services
        </button>

        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <h2 style={{ margin: "0 0 8px 0" }}>Add Service</h2>
          <p style={{ margin: 0, color: "#666" }}>
            Create a new service offering
          </p>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <div className="dashboard-card" style={{ padding: "30px" }}>
          <form onSubmit={handleSubmit}>
            {/* Service Name */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Service Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="e.g., Haircut & Style"
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                placeholder="Describe what this service includes..."
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.description.length}/500 characters
              </p>
            </div>

            {/* Price and Duration */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {formData.pricingType !== "range" &&
                formData.pricingType !== "hourly" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Price (USD) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="0.00"
                />
              </div>
              )}

              {formData.pricingType !== "hourly" &&
                formData.pricingType !== "range" && (
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Duration (minutes) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="durationMinutes"
                  value={formData.durationMinutes}
                  onChange={handleInputChange}
                  required
                  min="5"
                  step="5"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="30"
                />
              </div>
              )}
            </div>

            {/* Pricing Type */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Pricing Type
              </label>
              <select
                name="pricingType"
                value={formData.pricingType}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                <option value="fixed">Fixed Price</option>
                <option value="per_unit">Per Unit (e.g. per nail)</option>
                <option value="hourly">Hourly (rate x hours)</option>
                <option value="range">Options (customer picks one)</option>
                {/* From / Variable hidden until properly wired */}
              </select>
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.pricingType === "fixed" &&
                  "Exact price for the service"}
                {formData.pricingType === "from" &&
                  "Starting price, final may vary based on requirements"}
                {formData.pricingType === "range" &&
                  "Price varies within a range based on service complexity"}
                {formData.pricingType === "hourly" &&
                  "Charged per hour of work"}
                {formData.pricingType === "variable" &&
                  "Price determined after consultation"}
                {formData.pricingType === "per_unit" &&
                  "Price is per unit (e.g. per nail). Enter the per-unit price above and the time per unit as the duration. Customers choose the quantity."}
              </p>
            </div>

            {/* Price options - Only for range pricing (customer picks one) */}
            {formData.pricingType === "range" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Price options
                </label>
                <PriceTiersEditor
                  tiers={formData.priceTiers || []}
                  onChange={(nextTiers) =>
                    setFormData((prev) => ({ ...prev, priceTiers: nextTiers }))
                  }
                />
              </div>
            )}

            {/* Hourly Rate - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Hourly Rate (USD) <span style={{ color: "#c00" }}>*</span>
                </label>
                <input
                  type="number"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                  placeholder="Rate per hour"
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  The appointment length is the number of hours the customer
                  books (hours x 60 minutes).
                </p>
              </div>
            )}

            {/* Max hours - Only for hourly pricing */}
            {formData.pricingType === "hourly" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Max hours per booking
                </label>
                <input
                  type="number"
                  name="maxHoursPerBooking"
                  value={formData.maxHoursPerBooking}
                  onChange={handleInputChange}
                  min="1"
                  max="24"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                  }}
                />
                <p
                  style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
                >
                  Customers pick how many hours they need. Total price = hourly
                  rate x hours, and the appointment length scales the same way.
                </p>
              </div>
            )}

            {/* Requires Consultation - Only for variable pricing */}
            {formData.pricingType === "variable" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#001d4a",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    name="requiresConsultation"
                    checked={formData.requiresConsultation}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        requiresConsultation: e.target.checked,
                      }))
                    }
                    style={{ width: "18px", height: "18px" }}
                  />
                  Requires consultation before booking
                </label>
                <p
                  style={{
                    margin: "5px 0 0 28px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Customer will need to contact you for a quote before booking
                </p>
              </div>
            )}

            {/* Category */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={loadingCategories || serviceCategories.length === 0}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#fff",
                  cursor: "pointer",
                  boxSizing: "border-box",
                }}
              >
                {loadingCategories ? (
                  <option value="" disabled>
                    Loading categories...
                  </option>
                ) : serviceCategories.length === 0 ? (
                  <option value="" disabled>
                    No categories available
                  </option>
                ) : (
                  serviceCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Applicable To (Professional Types) */}
            {staffProfessionalTypes.length > 1 && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#001d4a",
                  }}
                >
                  Service Applies To
                </label>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "10px",
                  }}
                >
                  Leave empty if this service applies to all your professional
                  types. Otherwise, select which types (max 3).
                </p>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {staffProfessionalTypes.map((type) => (
                    <label
                      key={type}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px 12px",
                        border: "2px solid",
                        borderColor: formData.applicableTo.includes(type)
                          ? "#001d4a"
                          : "#ddd",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: formData.applicableTo.includes(type)
                          ? "#e8f4f8"
                          : "white",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.applicableTo.includes(type)}
                        onChange={() => handleApplicableToToggle(type)}
                        disabled={
                          formData.applicableTo.length >= 3 &&
                          !formData.applicableTo.includes(type)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: "500" }}>
                        {toUserFriendly(type)}
                      </span>
                    </label>
                  ))}
                </div>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Selected {formData.applicableTo.length} type
                  {formData.applicableTo.length !== 1 ? "s" : ""}
                </p>
              </div>
            )}

            {/* Service Image Upload - COMMENTED OUT PER USER REQUEST
            <div style={{ marginBottom: "30px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Service Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  cursor: "pointer",
                }}
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                Optional: Upload an image showcasing this service (max 5MB)
              </p>

              {/* Image Preview */}
            {/* {imagePreview && (
                <div style={{ marginTop: "15px", textAlign: "center" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "300px",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      border: "2px solid #e0e0e0",
                    }}
                  />
                </div>
              )} */}
            {/* </div> */}

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: "12px 30px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#333",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploadingImage}
                className="btn-primary"
                style={{
                  padding: "12px 30px",
                  minWidth: "140px",
                }}
              >
                {uploadingImage
                  ? "Uploading Image..."
                  : saving
                    ? "Creating..."
                    : "Create Service"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerServiceAdd;
